import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Dossier,
  Fitter,
  getGlassNameDefaultKey,
  getOperationWithGlassKey,
  OperationType,
  SinisterGlass,
} from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import {
  FITTER_NAME_DISPLAY_CONTEXT,
  FitterNameDisplayContext,
  FitterNameDisplayService,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { DeclarationConditions, ScreenUtils } from '@sc/utils';
import { forkJoin, merge, of } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { CollapseDirection, CollapseElementDirective } from '../../directives';
import { MomentParseZonePipe } from '../../pipes';
import { IconComponent } from '../icon';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';

@Component({
  selector: 'sc-dossier-fixed-summary',
  styleUrls: ['./dossier-fixed-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FitterNameDisplayService,
    {
      provide: FITTER_NAME_DISPLAY_CONTEXT,
      useValue: FitterNameDisplayContext.DossierFixedSummary,
    },
  ],
  imports: [
    NgIf,
    NgFor,
    IconComponent,
    MomentParseZonePipe,
    PhoneNumberPipe,
    FitterNamePipe,
    CollapseElementDirective,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
  ],
  template: `
    <div
      class="summary-container"
      [scCollapse]="!displaySummaryContent"
      [collapseDirection]="getCollapseDirection()"
      [reboundYOnCollapse]="5"
      [reboundXOnCollapse]="5"
    >
      <div class="summary-header">
        <span class="summary-line">
          <span
            class="main-arrow"
            [class.force-animation]="forceArrowAnim"
            (click)="changeChoice()"
            [attr.data-testid]="'close-summary-button'"
            ><sc-icon
              [class.left_to_right]="displaySummaryContent"
              [class.right_to_left]="!displaySummaryContent"
              [name]="
                displaySummaryContent ? 'big_arrow_right' : 'big_arrow_left'
              "
            ></sc-icon
          ></span>

          <h1
            [class.display-content]="
              !isMobileResolution() || displaySummaryContent
            "
          >
            {{ 'DossierFixedSummary.linkTitle' | translate }}
          </h1>
        </span>
      </div>
      <div
        *ngIf="hasInfosToDisplay()"
        class="summary-content"
        [class.display-content]="displaySummaryContent"
      >
        <h2>{{ 'DossierFixedSummary.appointmentBlocTitle' | translate }}</h2>
        <span class="summary-line" *ngIf="isGlassSet()">
          <sc-icon name="accident"></sc-icon>
          <div class="glasses-summary-container">
            <p>{{ getSinisterText() }}</p>
            <p
              *ngFor="
                let glassTranslation of getAdditionalSinisterText() | async
              "
            >
              {{ glassTranslation }}
            </p>
          </div>
        </span>
        <span
          class="summary-line"
          *ngIf="!isGlassChoiceFilled() && isImpactTypeFilled()"
        >
          <sc-icon name="impact"></sc-icon>
          <p>{{ getImpactTypeText() }}</p>
        </span>
        <span
          class="summary-line"
          *ngIf="!isGlassChoiceFilled() && isImpactSizeFilled()"
        >
          <sc-icon [name]="dossier?.claim?.glass?.impacts?.size"></sc-icon>
          <p>{{ getImpactSizeText() }}</p>
        </span>
        <span class="summary-line" *ngIf="isCarInformationsFilled()">
          <sc-icon name="car"></sc-icon>
          <span>
            <p *ngIf="this.dossier?.claim?.car?.manufacturer">
              {{ getVehicleText() }}
            </p>
            <p>{{ getVehicleLicensePlateText() }}</p>
          </span>
        </span>
        <span class="summary-line" *ngIf="isAppointmentFilled()">
          <sc-icon name="geoloc"></sc-icon>
          <span>
            <p>
              {{ getInterventionLocationText() }} <br />
              {{ getInterventionLocationAdress() }}
            </p>
          </span>
        </span>
        <span class="summary-line" *ngIf="isAppointmentFilled()">
          <sc-icon name="calendar_hour"></sc-icon>
          <span>
            <p>
              {{
                'GlobalApplicativeConstants.time.dateFormat'
                  | translate
                    : {
                        dateDay:
                          dossier?.appointment?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(dossier?.appointment)
                          | localeDate: 'fullDate'
                          | async,
                        isHalfDay: dossierSB.shouldDisplayAppointmentHalfDay(
                          dossier?.appointment
                        ),
                        dateTime:
                          dossier?.appointment?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(dossier?.appointment)
                          | localeDate: 'shortTime'
                          | async,
                        appointmentHalfDay: dossierSB.getTime(
                          dossier?.appointment
                        )
                      }
              }}
            </p>
          </span>
        </span>
      </div>
      <div
        *ngIf="fitter"
        class="summary-content"
        [class.display-content]="displaySummaryContent"
      >
        <h2>{{ 'DossierFixedSummary.fitterBlocTitle' | translate }}</h2>

        <h3>{{ fitter | fitterName | async }}</h3>
        <span class="summary-line">
          <sc-icon name="geoloc"></sc-icon>
          <span>
            <p>{{ getFitterAdress() }}</p>
          </span>
        </span>
        <span class="summary-line">
          <sc-icon name="phone"></sc-icon>
          <p>{{ getPhoneNumber() | phoneNumber | async }}</p>
        </span>
      </div>
    </div>
  `,
})
export class DossierFixedSummaryComponent implements OnInit, AfterViewInit {
  @Input() dossier: Dossier;
  @Input() fitter: Fitter;
  @Input() displaySummaryContent = false;
  @Input() forceArrowAnim = false;
  @Input() autoAnimOnInit: number[] = [];

  @Output() choiceMadeEvent = new EventEmitter<void>();

  isMobileResolution = ScreenUtils.isMobileResolution;

  isComponentInit = false;
  remainingAutoAnimOnInit: number[] = [];
  forceNoAnimCollapse = false;

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public dossierSB: DossierSandboxService,
    public timezoneService: TimezoneService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly configurationService: ConfigurationService,
    private readonly featureFlippService: FeatureFlippingManagerService
  ) {}

  ngOnInit(): void {
    this.remainingAutoAnimOnInit = [...this.autoAnimOnInit];
  }

  ngAfterViewInit(): void {
    this.isComponentInit = true;
    this.changeDetectorRef.detectChanges();

    if (this.remainingAutoAnimOnInit.length) {
      // We must fake a first openning in order to have the first automatic one to trigger animations
      // This is due to the fact that we avoid doing animations if forceArrowAnim is true, and it is until after the first open
      // So we trigger it 2 times (open + close) to avoid any visual change and still set forceArrowAnim to false
      this.forceNoAnimCollapse = true;
      this.changeChoice();
      this.changeChoice();
      this.forceNoAnimCollapse = false;

      merge(
        ...this.remainingAutoAnimOnInit.map((autoAnimMili) =>
          of(null).pipe(delay(autoAnimMili))
        )
      ).subscribe(() => {
        this.changeChoice();
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  getCollapseDirection(): CollapseDirection {
    if (this.forceArrowAnim || this.forceNoAnimCollapse) {
      return 'none';
    }

    const hasXCollapse = this.isMobileResolution();
    const hasYCollapse = this.hasInfosToDisplay();

    if (hasXCollapse && hasYCollapse) {
      return 'both';
    } else if (hasXCollapse) {
      return 'x';
    } else if (hasYCollapse) {
      return 'y';
    } else {
      return 'none';
    }
  }

  getSinisterText() {
    let text = '';
    if (this.isGlassChoiceFilled()) {
      text = this.getGlassNameWithOperation(
        this.dossier?.claim?.glass,
        this.dossier?.claim?.operationType
      );
      if (
        this.dossier?.claim?.glass?.glass === 'windshield' &&
        this.dossier.claim.operationType === 'Replace' &&
        this.dossier.claim.calibration === 'true'
      ) {
        text += this.translatorService.getInstantTranslation(
          'DossierFixedSummary.withCalibrationText'
        );
        if (this.dossier?.appointment?.isReplacementVehicleSelected) {
          text += this.translatorService.getInstantTranslation(
            'DossierFixedSummary.withVehicleText'
          );
        }
      } else if (this.dossier?.appointment?.isReplacementVehicleSelected) {
        text += this.translatorService.getInstantTranslation(
          'DossierFixedSummary.withOnlyVehicleText'
        );
      }
    } else text = this.getGlassName(this.dossier?.claim?.glass);

    return text;
  }

  getAdditionalSinisterText() {
    const usedGlasses: SinisterGlass[] = this.dossier.custom?.additionalGlasses;

    return usedGlasses?.length > 0
      ? forkJoin(
          usedGlasses
            .filter(
              (glass, i) =>
                usedGlasses.findIndex(
                  (sameGlass, j) =>
                    sameGlass.glass === glass.glass &&
                    ((!sameGlass.glassExtended && !glass.glassExtended) ||
                      sameGlass.glassExtended === glass.glassExtended) &&
                    j < i
                ) === -1 &&
                (glass.glass !== this.dossier?.claim?.glass?.glass ||
                  !(
                    (!glass.glassExtended &&
                      !this.dossier?.claim?.glass?.glassExtended) ||
                    glass.glassExtended ===
                      this.dossier.claim.glass.glassExtended
                  ))
            )
            .map((glass) =>
              this.translatorService
                .getTranslation(getGlassNameDefaultKey(glass))
                .pipe(take(1))
            )
        )
      : of([]);
  }

  getGlassNameWithOperation(
    glass: SinisterGlass,
    operationType: OperationType
  ): string {
    return this.translatorService.getInstantTranslation(
      getOperationWithGlassKey(operationType),
      { glassName: this.getGlassName(glass) }
    );
  }

  getGlassName(glass: SinisterGlass): string {
    return this.translatorService.getInstantTranslation(
      getGlassNameDefaultKey(glass)
    );
  }

  getInterventionLocationAdress() {
    return this.dossier?.appointment?.interventionLocation?.address;
  }

  getInterventionLocationText() {
    return this.dossier?.appointment?.outsideIntervention
      ? this.translatorService.getInstantTranslation(
          'DossierFixedSummary.outside'
        )
      : this.translatorService.getInstantTranslation(
          'DossierFixedSummary.in_center'
        );
  }

  getImpactTypeText() {
    return this.translatorService.getInstantTranslation(
      'GlobalApplicativeConstants.sinisterGlass.glassImpact.' +
        this.dossier?.claim?.glass?.impacts?.impactsType
    );
  }

  getImpactSizeText() {
    return this.translatorService.getInstantTranslation(
      'GlobalApplicativeConstants.sinisterGlass.glassImpact.' +
        this.dossier?.claim?.glass?.impacts?.size
    );
  }

  getFitterAdress() {
    return (
      this.fitter?.address + ', ' + this.fitter?.cp + ', ' + this.fitter?.city
    );
  }

  getVehicleText() {
    return (
      this.dossier?.claim?.car?.manufacturer +
      ' ' +
      this.dossier?.claim?.car?.model
    );
  }

  getVehicleLicensePlateText() {
    return this.dossier?.claim?.car?.licensePlateNumber;
  }

  getPhoneNumber() {
    return this.featureFlippService?.isFeatureActivated('b2c-display-gmb-phone')
      ? this.configurationService.getConfig()?.phone
      : this.fitter?.phone;
  }

  isCarInformationsFilled() {
    return this.declarationConditions.hasDisplayableCarInformations(
      this.dossier
    );
  }

  isGlassChoiceFilled() {
    return this.declarationConditions.isGlassChoiceFilled(this.dossier);
  }

  isGlassSet() {
    return this.declarationConditions.isGlassSet(this.dossier?.claim?.glass);
  }

  isAppointmentFilled() {
    return this.declarationConditions.hasValidAppointment(this.dossier);
  }

  isImpactTypeFilled() {
    return this.declarationConditions.hasWindshieldImpactType(
      this.dossier?.claim?.glass
    );
  }

  isImpactSizeFilled() {
    return this.declarationConditions.hasWindshieldImpactSize(
      this.dossier?.claim?.glass
    );
  }

  hasInfosToDisplay() {
    return (
      this.isGlassSet() ||
      this.isCarInformationsFilled() ||
      this.isAppointmentFilled()
    );
  }

  changeChoice() {
    this.displaySummaryContent = !this.displaySummaryContent;
    this.choiceMadeEvent.emit();
  }
}
